import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer, X } from 'lucide-react';
import {
  formatIqd,
  fromIqd,
  fromMillidinars,
  subtract,
  type BucketAmounts,
  type BucketPercents,
  type Millidinars,
  type ReceiptDto,
  type RoundingRule,
  type TabDetailDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { api, ApiError } from '@/lib/api';
import { checkoutFailureMessage } from './checkoutFailure';
import {
  buildRoundsPayload,
  DEFAULT_ROUNDS_ENTRY,
  MAX_ROUNDS,
  openRoundSessions,
  previewCheckout,
  serviceChargeSign,
  type CheckoutPreviewOrder,
  type CheckoutRoundType,
  type CheckoutRoundsEntry,
  type CheckoutSelection,
} from './checkoutPreview';
import { ReceiptView } from './ReceiptView';
import { uploadRaster } from './rasterUpload';
import { receiptToRaster } from './receiptCanvas';
import {
  CASH_TENDER,
  TenderPicker,
  tenderBody,
  useElectronicTenders,
  type Tender,
} from './TenderPicker';

// The pure half lives in `checkoutPreview.ts` now, where it can be pinned
// without React; re-exported so every existing import of it keeps working.
export {
  buildRoundsPayload,
  DEFAULT_ROUNDS_ENTRY,
  MAX_ROUNDS,
  openRoundSessions,
  roundsPreviewMd,
  type CheckoutRoundType,
  type CheckoutRoundsEntry,
  type CheckoutSelection,
} from './checkoutPreview';

interface TabCheckoutDrawerProps {
  tabId: string;
  currencySymbol: string;
  /** Settle only these rows; omit to settle the whole party. */
  selection?: CheckoutSelection | null;
  onClose: () => void;
  onSettled: () => void;
}

/**
 * Wave-3 additions the tabs endpoint returns on top of the (frozen this wave)
 * shared `TabDetailDto`: the customer's four bucket percents and the bases
 * the checkout will discount, so the preview below runs the SAME
 * `computeBucketDiscounts` the charge runs. Mirrors `TabBucketPreview` on
 * `apps/api/src/tabs/tabs.service.ts`.
 */
interface TabCheckoutDetail extends TabDetailDto {
  bucketPercents: BucketPercents | null;
  timeBaseMd: string;
  cafeBaseMd: string;
  kitchenBaseMd: string;
  shishaBaseMd: string;
  /** Honest play total (per-session rounded, post-discount) — what settles. */
  playTotalMd: string;
  ordersTotalMd: string;
  /**
   * Per-bill bases, so a PARTIAL settlement previews with the right shares —
   * and, since the service charge, each bill's charge and the bases with it
   * riding in them.
   */
  orders: CheckoutPreviewOrder[];
  /** §6.1 rule, so the drawer's Due runs the same grand-total rounding pass. */
  roundingRule: RoundingRule;
  /** The cash step that pass lands on, in whole IQD; 0 means whole dinars. */
  checkoutRoundingStepIqd: number;
}

const NO_PERCENTS: BucketPercents = {
  timePercent: 0,
  cafePercent: 0,
  kitchenPercent: 0,
  shishaPercent: 0,
};

/** The three discount buckets, in the order they appear on the cheque. */
export const BUCKET_KEYS = ['time', 'cafe', 'kitchen', 'shisha'] as const;
export type BucketKey = (typeof BUCKET_KEYS)[number];

/**
 * Settle a whole party (SPEC §5.3).
 *
 * Shows every device on the tab with its running cost, takes the payment, then
 * swaps to the combined receipt — the one long ticket the group leaves with.
 * The amount-due preview applies the customer's split discount with the same
 * shared function the server charges with, so preview == charge.
 */
export function TabCheckoutDrawer({
  tabId,
  currencySymbol,
  selection = null,
  onClose,
  onSettled,
}: TabCheckoutDrawerProps) {
  const { t } = useTranslation();
  const { can } = useAuth();
  /*
   * Settling writes the bills, so the route wants `bills.create` on top of
   * `sessions.manage` (2026-09-24) — and the screen that opens this drawer
   * gates on the second alone. Without the first, no provider is offered and
   * Settle stays shut, with the reason: a cashier told to "take the payment
   * in the FIB app first" and then refused would leave the customer's money
   * in FIB with nothing recorded against it. With cash the notes simply go
   * back across the counter; with FIB they have already moved.
   */
  const canTakePayment = can('bills.create');

  const [tab, setTab] = useState<TabCheckoutDetail | null>(null);
  const [receipt, setReceipt] = useState<ReceiptDto | null>(null);
  /** Cash, the balance, or a switched-on payment provider (2026-09-24). */
  const [tender, setTender] = useState<Tender>(CASH_TENDER);
  const method = tender.method;
  const { providers, failed: providersFailed } = useElectronicTenders();
  const [discountIqd, setDiscountIqd] = useState('0');
  /** A percentage off the whole party, on top of any haggled amount. */
  const [discountPercent, setDiscountPercent] = useState('0');
  /*
   * Six fields, one pair per bucket (owner decision: percent AND amount, side by
   * side). Percents start EMPTY and empty means "leave the customer's own alone" —
   * typing 0 is how you say "none today" for someone who normally gets one, so the
   * two cannot share a representation.
   */
  const [bucketPercentText, setBucketPercentText] = useState<Record<BucketKey, string>>({
    time: '',
    cafe: '',
    kitchen: '',
    shisha: '',
  });
  const [bucketAmountText, setBucketAmountText] = useState<Record<BucketKey, string>>({
    time: '',
    cafe: '',
    kitchen: '',
    shisha: '',
  });
  const [tenderedIqd, setTenderedIqd] = useState('');
  /**
   * "Remove service charge" (owner, 2026-09-23: "the cashier could remove the
   * service for any customer they want"). Off by default — the venue's charge
   * applies unless somebody at the counter decides otherwise, and the server
   * audits every waive that gave money up.
   */
  const [serviceChargeWaived, setServiceChargeWaived] = useState(false);
  const [roundEntries, setRoundEntries] = useState<Record<string, CheckoutRoundsEntry>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setTab(await api.get<TabCheckoutDetail>(`/tabs/${tabId}`));
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : t('checkout.loadFailed'));
      }
    })();
  }, [tabId, t]);

  const accountPercents = tab?.bucketPercents ?? NO_PERCENTS;
  const overridden = (key: BucketKey, fallback: number): number => {
    const raw = bucketPercentText[key].trim();
    if (raw === '') return fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? Math.max(0, Math.min(100, Math.floor(parsed))) : fallback;
  };
  const percents: BucketPercents = {
    timePercent: overridden('time', accountPercents.timePercent),
    cafePercent: overridden('cafe', accountPercents.cafePercent),
    kitchenPercent: overridden('kitchen', accountPercents.kitchenPercent),
    shishaPercent: overridden('shisha', accountPercents.shishaPercent ?? 0),
  };
  const typedAmountIqd = (key: BucketKey): number =>
    Math.max(0, Math.floor(Number(bucketAmountText[key]) || 0));
  const bucketAmounts: BucketAmounts = {
    timeMd: fromIqd(typedAmountIqd('time')),
    cafeMd: fromIqd(typedAmountIqd('cafe')),
    kitchenMd: fromIqd(typedAmountIqd('kitchen')),
    shishaMd: fromIqd(typedAmountIqd('shisha')),
  };

  /*
   * The whole quote, in one pure call — `checkoutPreview.ts` restates the
   * server's checkout step for step (selection, rounds, service charge,
   * buckets, the counter's percent and amount, then the one rounding pass),
   * and is pinned there against the same worked figures the API suite drives
   * the real checkout with.
   */
  const preview = previewCheckout(
    tab ?? { sessions: [], orders: [], roundingRule: 'AVERAGE', checkoutRoundingStepIqd: 0 },
    {
      selection,
      roundEntries,
      percents,
      bucketAmounts,
      discountPercent: Number(discountPercent) || 0,
      discountIqd: Number(discountIqd) || 0,
      serviceChargeWaived,
    },
  );
  const { settleSessions, ordersMd, owedMd, buckets, rawDueMd, dueMd } = preview;
  const roundSessions = openRoundSessions(settleSessions);
  /**
   * What the step moved, shown on its own line.
   *
   * Not cosmetic: the box is reconciled against the bills, and a total that
   * silently differs from its parts is a discrepancy somebody has to chase at
   * the end of a shift. It also gives a cashier the one sentence they need when
   * a customer asks why 2,220 is 2,250.
   */
  const roundingMd = preview.roundingMd;
  /*
   * The service charge's row and its switch appear only when there is one to
   * speak of (owner: "if the service is 0% it should not be mentioned"): a 0%
   * venue, or a selection wholly outside the scope, shows neither.
   */
  const serviceChargePercent = tab?.serviceCharge?.percent ?? 0;
  const offersServiceCharge = serviceChargePercent > 0 && preview.serviceChargeAvailableMd !== 0n;
  // Gated on CASH, mirroring the wire body below: the Tendered box is disabled
  // on ACCOUNT_BALANCE but keeps whatever was typed, so without this the row
  // went on quoting change out of a drawer nobody was opening.
  const changeMd =
    method === 'CASH' && tenderedIqd ? subtract(fromIqd(Number(tenderedIqd) || 0), dueMd) : null;

  const bucketRows = [
    {
      key: 'time',
      label: 'receipt.discountReservations',
      percent: percents.timePercent,
      amount: buckets.timeDiscountMd,
    },
    {
      key: 'cafe',
      label: 'receipt.discountCafe',
      percent: percents.cafePercent,
      amount: buckets.cafeDiscountMd,
    },
    {
      key: 'kitchen',
      label: 'receipt.discountKitchen',
      percent: percents.kitchenPercent,
      amount: buckets.kitchenDiscountMd,
    },
    {
      key: 'shisha',
      label: 'receipt.discountShisha',
      percent: percents.shishaPercent ?? 0,
      amount: buckets.shishaDiscountMd,
    },
  ].filter((row) => row.amount !== 0n);

  /*
   * The customer's own percent and the applied amount, per bucket. Keyed lookups
   * rather than the chain of ternaries this replaced: with four buckets the
   * chain's final branch silently became the catch-all for whatever bucket was
   * added last, which is how a new bucket would show another's figures.
   */
  const ownPercentOf: Record<BucketKey, number> = {
    time: accountPercents.timePercent,
    cafe: accountPercents.cafePercent,
    kitchen: accountPercents.kitchenPercent,
    shisha: accountPercents.shishaPercent ?? 0,
  };
  const appliedOf: Record<BucketKey, Millidinars> = {
    time: buckets.timeDiscountMd,
    cafe: buckets.cafeDiscountMd,
    kitchen: buckets.kitchenDiscountMd,
    shisha: buckets.shishaDiscountMd,
  };

  async function handleCheckout(event: FormEvent) {
    event.preventDefault();
    if (!canTakePayment) return;
    setError(null);
    setSubmitting(true);

    try {
      const result = await api.post<ReceiptDto>(`/tabs/${tabId}/checkout`, {
        // `{ method }` for cash and balance, exactly as before; the provider
        // id rides along only with an electronic tender.
        ...tenderBody(tender),
        discountIqd: Number(discountIqd) || 0,
        discountPercent: Number(discountPercent) || 0,
        buckets: {
          time: { percent: percents.timePercent, amountIqd: typedAmountIqd('time') },
          cafe: { percent: percents.cafePercent, amountIqd: typedAmountIqd('cafe') },
          kitchen: { percent: percents.kitchenPercent, amountIqd: typedAmountIqd('kitchen') },
          shisha: { percent: percents.shishaPercent ?? 0, amountIqd: typedAmountIqd('shisha') },
        },
        // The billiard tables' games, keyed by session id — without this the
        // server falls back to the row counters, which are 0 on any open
        // session, and every ROUND device on the tab settles free.
        rounds: buildRoundsPayload(settleSessions, roundEntries),
        /*
         * Only ever "waive it" or "charge it": the rate is the venue's, never
         * the till's to send (see `checkoutTabSchema.serviceChargeWaived`).
         *
         * And only when this drawer QUOTED a charge. Its snapshot of the rate
         * is the one read on open; an explicit `false` sent from a drawer that
         * showed 0% was charged at whatever the rate is at commit, so an owner
         * switching the charge on while the drawer sat open put money on the
         * receipt the cashier never collected. Left out, the server charges
         * nothing — which is what this screen told the customer (see the
         * three-valued note in `TabsService.checkout`). A rate that changes
         * between two non-zero values mid-drawer is still charged at the new
         * one: the schema has no field to carry the quoted percent.
         */
        ...(offersServiceCharge ? { serviceChargeWaived } : {}),
        /*
         * ALWAYS SENT. This used to be omitted when the ticked rows looked like
         * the whole tab, which took the server's uncapped settle-everything
         * path — and that was a way to charge for rows nobody ticked. The
         * drawer reads `GET /tabs/:id` ONCE, on open, and never again; a cafe
         * bill rung up on the same tab while the drawer sits open is simply not
         * in that snapshot, so the check said "whole tab", the selection was
         * dropped, and the server settled the new bill too — marking it PAID
         * against money the cashier never took.
         *
         * An explicit selection is the only description of the settle set that
         * cannot widen between render and commit. The cap it has to fit inside
         * is 500, and the house tab that started all this had 342.
         */
        ...(selection
          ? {
              selection: {
                sessionIds: [...selection.sessionIds],
                orderBillIds: [...selection.orderBillIds],
              },
            }
          : {}),
        /*
         * Cash only, and clamped — the same idiom as `typedAmountIqd` above.
         *
         * The server ignores what was tendered on any other method, so sending
         * it was never anything but schema surface. And the raw `Number()` put
         * whatever the box held straight onto the wire: a long pasted digit
         * string becomes Infinity, which `JSON.stringify` writes as null, and
         * the refusal is once again a sentence that names no field.
         */
        ...(method === 'CASH' && tenderedIqd
          ? {
              tenderedIqd: Math.min(
                Number.MAX_SAFE_INTEGER,
                Math.max(0, Math.floor(Number(tenderedIqd) || 0)),
              ),
            }
          : {}),
      });
      setReceipt(result);
      onSettled();
      void sendDrawnReceipt(result);
    } catch (caught) {
      // The refusal WITH its fields — see `checkoutFailure.ts`. Keeping only
      // the headline is how a refused checkout became undiagnosable from in
      // front of the till.
      setError(checkoutFailureMessage(caught, t('checkout.failed')));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Draw the receipt and offer it to the job that is already queued.
   *
   * FIRE AND FORGET, AND SILENT ON FAILURE — deliberately. The money has
   * already moved and the job already carries a typed body, so every way this
   * can fail (an old browser with no canvas, a receipt too long for the wire, a
   * dropped request, the drawer closed before it finished) ends with the
   * customer getting the typed receipt they would have got anyway. Surfacing an
   * error here would be telling a cashier something went wrong when nothing
   * they can act on has.
   *
   * It is called AFTER `onSettled()` so the screen clears at its usual speed:
   * drawing must never sit between the cashier and the next customer.
   */
  async function sendDrawnReceipt(settled: ReceiptDto): Promise<void> {
    if (!settled.printJobId) return;
    try {
      const raster = receiptToRaster(settled);
      if (!raster) return;
      // Deflated when the browser can: a long tab is up to 1.5 MB raw.
      await uploadRaster(settled.printJobId, raster);
    } catch {
      // See above: the typed receipt prints regardless.
    }
  }

  const fieldClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none focus:border-primary tabular';

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} role="presentation" />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('checkout.title')}
        className="fixed inset-y-0 z-50 flex w-full max-w-md flex-col border-s border-subtle bg-ground shadow-2xl"
        style={{ insetInlineEnd: 0 }}
      >
        <header className="flex items-center gap-3 border-b border-subtle p-4">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="rounded-control p-1 text-content-secondary hover:bg-hover hover:text-content"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
          <h2 className="flex-1 truncate text-base font-semibold text-content">
            {receipt ? t('checkout.receipt') : t('checkout.title')}
            {tab ? ` — ${tab.name}` : ''}
          </h2>
          {receipt ? (
            <button
              type="button"
              onClick={() => window.print()}
              className="flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary hover:bg-hover"
            >
              <Printer className="h-4 w-4" aria-hidden />
              {t('common.print')}
            </button>
          ) : null}
        </header>

        {receipt ? (
          <div className="min-h-0 flex-1 overflow-auto p-4">
            <ReceiptView receipt={receipt} />
            <button
              type="button"
              onClick={onClose}
              className="mt-4 w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-fg hover:bg-primary-hover"
            >
              {t('common.confirm')}
            </button>
          </div>
        ) : (
          <form
            onSubmit={(event) => void handleCheckout(event)}
            className="flex min-h-0 flex-1 flex-col overflow-auto p-4"
          >
            {!tab ? (
              <p className="text-sm text-content-muted">{t('common.loading')}</p>
            ) : (
              <>
                <ul className="mb-4 divide-y divide-subtle rounded-card border border-subtle">
                  {tab.sessions.map((session) => (
                    <li key={session.id} className="flex items-center justify-between px-3 py-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm text-content">{session.deviceName}</div>
                        <div className="text-xs text-content-muted">
                          {t(`sessions.status.${session.status.toLowerCase()}`, {
                            defaultValue: session.status,
                          })}
                        </div>
                      </div>
                      <span className="tabular text-sm text-content-secondary">
                        {formatIqd(fromMillidinars(session.amountMd), { currencySymbol })}
                      </span>
                    </li>
                  ))}
                </ul>

                {roundSessions.length > 0 ? (
                  <div className="mb-4 space-y-3">
                    {roundSessions.map((session) => {
                      const entry = roundEntries[session.id] ?? DEFAULT_ROUNDS_ENTRY;
                      return (
                        <div key={session.id} className="grid grid-cols-3 items-end gap-3">
                          <div className="min-w-0 truncate pb-2.5 text-sm text-content">
                            {session.deviceName}
                          </div>
                          <label className="block">
                            <span className="mb-1 block text-xs text-content-secondary">
                              {t('sessions.roundsPlayed')}
                            </span>
                            <input
                              type="number"
                              min="0"
                              // The schema's own ceiling. With it, the browser
                              // points at this box; without it the server
                              // answered with a sentence naming no field.
                              max={MAX_ROUNDS}
                              step="1"
                              value={entry.rounds}
                              onChange={(event) =>
                                setRoundEntries((previous) => ({
                                  ...previous,
                                  [session.id]: { ...entry, rounds: event.target.value },
                                }))
                              }
                              className={fieldClass}
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-xs text-content-secondary">
                              {t('sessions.roundType')}
                            </span>
                            <select
                              value={entry.type}
                              onChange={(event) =>
                                setRoundEntries((previous) => ({
                                  ...previous,
                                  [session.id]: {
                                    ...entry,
                                    type: event.target.value as CheckoutRoundType,
                                  },
                                }))
                              }
                              className={fieldClass}
                            >
                              <option value="SINGLE">{t('sessions.single')}</option>
                              <option value="DOUBLE">{t('sessions.double')}</option>
                              {session.threeBallPriceMd != null ? (
                                <option value="THREE_BALL">{t('sessions.threeBall')}</option>
                              ) : null}
                            </select>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {/*
                  Per-bucket discounts, percent and amount side by side (owner
                  decision). The percent box shows the customer's own as its
                  placeholder rather than its value: pre-filling the value would
                  make "leave their arrangement alone" and "set it to exactly
                  that" indistinguishable, and the cashier could not tell whether
                  they had already touched it.
                */}
                <div className="mb-4 rounded-control border border-subtle p-2.5">
                  <span className="mb-2 block text-xs font-medium text-content-secondary">
                    {t('checkout.bucketDiscounts')}
                  </span>
                  <div className="space-y-2">
                    {BUCKET_KEYS.map((key) => {
                      const ownPercent = ownPercentOf[key];
                      const applied = appliedOf[key];
                      return (
                        <div
                          key={key}
                          className="grid grid-cols-[1fr_4.5rem_6rem] items-center gap-2"
                        >
                          <span className="truncate text-xs text-content">
                            {t(`checkout.bucket.${key}`)}
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            inputMode="numeric"
                            data-bucket-percent={key}
                            value={bucketPercentText[key]}
                            placeholder={ownPercent > 0 ? `${ownPercent}%` : '%'}
                            aria-label={t('checkout.bucketPercentLabel', {
                              bucket: t(`checkout.bucket.${key}`),
                            })}
                            onChange={(event) =>
                              setBucketPercentText((previous) => ({
                                ...previous,
                                [key]: event.target.value,
                              }))
                            }
                            className={`${fieldClass} px-2 py-1.5 text-xs`}
                          />
                          <input
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            data-bucket-amount={key}
                            value={bucketAmountText[key]}
                            placeholder={t('checkout.amountPlaceholder')}
                            aria-label={t('checkout.bucketAmountLabel', {
                              bucket: t(`checkout.bucket.${key}`),
                            })}
                            onChange={(event) =>
                              setBucketAmountText((previous) => ({
                                ...previous,
                                [key]: event.target.value,
                              }))
                            }
                            className={`${fieldClass} px-2 py-1.5 text-xs`}
                          />
                          <span
                            data-bucket-applied={key}
                            className="tabular col-span-3 -mt-1 text-end text-[11px] text-content-muted"
                          >
                            {applied === 0n ? '' : `−${formatIqd(applied, { currencySymbol })}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mb-4 grid grid-cols-3 gap-3">
                  {/*
                    The counter's own percentage, off the WHOLE party — the
                    "give them 10% off the lot" conversation, as opposed to the
                    per-bucket percents above, which are the customer's standing
                    arrangement. It compounds: taken off what is left after
                    those, which is the only reading a cashier can defend when
                    the customer adds the numbers on the paper.
                  */}
                  <label className="block">
                    <span className="mb-1 block text-xs text-content-secondary">
                      {t('checkout.overallPercent')}
                    </span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      data-overall-percent
                      value={discountPercent}
                      onChange={(event) => setDiscountPercent(event.target.value)}
                      className={fieldClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-content-secondary">
                      {t('checkout.extraDiscount')}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={discountIqd}
                      onChange={(event) => setDiscountIqd(event.target.value)}
                      className={fieldClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-content-secondary">
                      {t('checkout.tendered')}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={tenderedIqd}
                      onChange={(event) => setTenderedIqd(event.target.value)}
                      disabled={method !== 'CASH'}
                      className={fieldClass}
                    />
                  </label>
                </div>

                {/*
                  The cashier's one decision about the service charge: take it
                  off for this customer, or leave it. No privilege needed (owner:
                  "the cashier could remove the service for any customer they
                  want"); the server records who removed how much.
                */}
                {offersServiceCharge ? (
                  <label className="mb-4 flex items-center justify-between gap-3 rounded-control border border-subtle px-3 py-2.5">
                    <span className="min-w-0">
                      <span className="block text-sm text-content">
                        {t('serviceCharge.remove')}
                      </span>
                      <span className="block text-xs text-content-muted">
                        {t('serviceCharge.removeHint', { percent: serviceChargePercent })}
                      </span>
                    </span>
                    <input
                      type="checkbox"
                      role="switch"
                      data-service-charge-waive
                      checked={serviceChargeWaived}
                      onChange={(event) => setServiceChargeWaived(event.target.checked)}
                      className="h-5 w-5 shrink-0 accent-[var(--brand-primary)]"
                    />
                  </label>
                ) : null}

                <div className="mb-4">
                  <TenderPicker
                    value={tender}
                    onChange={setTender}
                    providers={canTakePayment ? providers : []}
                    providersFailed={providersFailed}
                    allowAccountBalance
                  />
                </div>

                <dl className="rounded-card bg-raised p-3 text-sm">
                  {ordersMd !== 0n ? (
                    <Row
                      label={t('receipt.orders')}
                      value={formatIqd(ordersMd, { currencySymbol })}
                    />
                  ) : null}
                  <Row label={t('common.total')} value={formatIqd(owedMd, { currencySymbol })} />
                  {/* Between the total and the discounts, because that is
                      where it is charged: before any discount. */}
                  {offersServiceCharge ? (
                    <Row
                      label={t('serviceCharge.line', { percent: serviceChargePercent })}
                      value={
                        serviceChargeWaived ? (
                          <span data-service-charge-row="removed">
                            {t('serviceCharge.removed')}
                          </span>
                        ) : (
                          <span dir="ltr" data-service-charge-row="applied">
                            {serviceChargeSign(preview.serviceChargeMd)}
                            {formatIqd(preview.serviceChargeMd, { currencySymbol })}
                          </span>
                        )
                      }
                    />
                  ) : null}
                  {bucketRows.map((row) => (
                    <Row
                      key={row.key}
                      label={t(row.label, { percent: row.percent })}
                      value={<span dir="ltr">-{formatIqd(row.amount, { currencySymbol })}</span>}
                    />
                  ))}
                  {/* Only when the cash step actually moved something. A
                      "Rounding 0" line on every bill is noise; on the bills it
                      does appear on, it is the answer to "why 2,250?". */}
                  {roundingMd !== 0n ? (
                    <Row
                      label={t('checkout.rounding')}
                      value={
                        <span dir="ltr">
                          {roundingMd > 0n ? '+' : '-'}
                          {/* The magnitude, formed by subtraction rather than
                              negation so it stays a branded Millidinars. */}
                          {formatIqd(roundingMd > 0n ? roundingMd : subtract(rawDueMd, dueMd), {
                            currencySymbol,
                          })}
                        </span>
                      }
                    />
                  ) : null}
                  {/* The figure the cashier says out loud (card `.total b`):
                      display numerals, brand cyan, glowing — nothing else in
                      this drawer competes with it. */}
                  <div className="mt-1 flex items-baseline justify-between border-t border-strong pt-2">
                    <dt className="text-[10px] uppercase tracking-[0.22em] text-content-muted">
                      {t('checkout.due')}
                    </dt>
                    <dd className="tabular text-[26px] leading-tight font-bold text-primary [text-shadow:var(--glow-primary)]">
                      {formatIqd(dueMd, { currencySymbol })}
                    </dd>
                  </div>
                  {changeMd !== null ? (
                    <Row
                      label={t('checkout.change')}
                      value={formatIqd(changeMd, { currencySymbol })}
                    />
                  ) : null}
                </dl>
              </>
            )}

            {error ? (
              <p role="alert" className="mt-3 text-sm text-danger">
                {error}
              </p>
            ) : null}

            {!canTakePayment ? (
              <p data-needs-bills-create className="mt-3 text-sm text-content-muted">
                {t('tender.needsBillsCreate')}
              </p>
            ) : null}

            <div className="flex-1" />

            <button
              type="submit"
              disabled={submitting || !tab || !canTakePayment}
              className="mt-6 w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-fg hover:bg-primary-hover disabled:opacity-60"
            >
              {submitting ? t('common.loading') : t('checkout.settle')}
            </button>
          </form>
        )}
      </aside>
    </>
  );
}

function Row({
  label,
  value,
  strong,
}: {
  label: string;
  value: React.ReactNode;
  strong?: boolean;
}) {
  return (
    <div className="flex justify-between py-0.5">
      <dt className="text-content-secondary">{label}</dt>
      <dd className={`tabular ${strong ? 'font-semibold text-content' : 'text-content-secondary'}`}>
        {value}
      </dd>
    </div>
  );
}
